import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerRange } from "@/components/ui/date-picker";
import { FilterConfig, dateRangeFilter, numberRangeFilter } from "@/config/callHistoryFilters";

interface CustomFilterProps {
  config: FilterConfig[];
  values: Record<string, string[]>;
  onChange: (key: string, values: string[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CustomFilter({ config, values, onChange, open, onOpenChange }: CustomFilterProps) {
  const [pending, setPending] = useState<Record<string, string[]>>(values);

  useEffect(() => {
    if (open) setPending(values);
  }, [open]);

  const handleApply = () => {
    config.forEach((f) => {
      let vals = pending[f.key] ?? [];
      if (f.type === "number-range") {
        vals = vals.filter((v) => numberRangeFilter.decode(v).v1 !== "");
      }
      if (f.type === "text-input") {
        vals = vals.filter((v) => v.trim() !== "");
      }
      if (f.type === "date-range") {
        vals = vals.filter((v) => {
          const { from, to } = dateRangeFilter.decode(v);
          return from !== "" || to !== "";
        });
      }
      onChange(f.key, vals);
    });
    onOpenChange(false);
  };

  const handleCancel = () => {
    setPending(values);
    onOpenChange(false);
  };

  const toggle = (key: string, value: string, multi: boolean) => {
    setPending((prev) => {
      const current = prev[key] ?? [];
      if (multi) {
        return {
          ...prev,
          [key]: current.includes(value)
            ? current.filter((v) => v !== value)
            : [...current, value],
        };
      }
      return {
        ...prev,
        [key]: current.includes(value) ? [] : [value],
      };
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent side="right" className="!w-96 p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
          <SheetTitle>Filters</SheetTitle>
        </SheetHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div className="px-6 py-4 space-y-6">
            {config.map((filter) => (
              <div key={filter.key}>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-sm">{filter.header}</p>
                  {(pending[filter.key] ?? []).length > 0 && (
                    <button
                      type="button"
                      onClick={() => setPending((prev) => ({ ...prev, [filter.key]: [] }))}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {filter.type === "checkbox" && (
                  <div className="flex flex-col gap-2 max-h-48 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full">
                    {(filter.options ?? []).map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center gap-1.5 cursor-pointer text-sm"
                      >
                        <Checkbox
                          checked={(pending[filter.key] ?? []).includes(option.value)}
                          onCheckedChange={() =>
                            toggle(filter.key, option.value, filter.multiSelect ?? false)
                          }
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                )}

                {filter.type === "custom" &&
                  filter.render?.(pending[filter.key] ?? [], (vals) =>
                    setPending((prev) => ({ ...prev, [filter.key]: vals })),
                  )}

                {filter.type === "date-range" && (() => {
                  const encoded = (pending[filter.key] ?? [])[0] ?? "";
                  const { from: fromStr, to: toStr } = dateRangeFilter.decode(encoded);
                  const range = {
                    from: fromStr ? new Date(fromStr + "T00:00:00") : undefined,
                    to: toStr ? new Date(toStr + "T00:00:00") : undefined,
                  };

                  return (
                    <DatePickerRange
                      value={range.from || range.to ? range : undefined}
                      onChange={(r) => {
                        const f = r?.from ? format(r.from, "yyyy-MM-dd") : "";
                        const t = r?.to ? format(r.to, "yyyy-MM-dd") : "";
                        setPending((prev) => ({
                          ...prev,
                          [filter.key]: f || t ? [dateRangeFilter.encode({ from: f, to: t })] : [],
                        }));
                      }}
                    />
                  );
                })()}

                {filter.type === "text-input" && (
                  <Input
                    placeholder={filter.placeholder ?? "Search..."}
                    value={(pending[filter.key] ?? [])[0] ?? ""}
                    onChange={(e) =>
                      setPending((prev) => ({
                        ...prev,
                        [filter.key]: e.target.value ? [e.target.value] : [],
                      }))
                    }
                    className="bg-background"
                  />
                )}

                {filter.type === "number-range" && (() => {
                  const encoded = (pending[filter.key] ?? [])[0] ?? "";
                  const { op, v1, v2 } = numberRangeFilter.decode(encoded);

                  const update = (newOp: typeof op, newV1: string, newV2?: string) => {
                    setPending((prev) => ({
                      ...prev,
                      [filter.key]: [numberRangeFilter.encode({ op: newOp, v1: newV1, v2: newV2 })],
                    }));
                  };

                  const prefix = filter.unitPrefix;
                  const suffix = filter.unitSuffix;

                  return (
                    <div className="space-y-2">
                      <div className="flex rounded-md border overflow-hidden text-sm">
                        {(["gt", "lt", "between"] as const).map((optVal) => (
                          <button
                            key={optVal}
                            type="button"
                            className={`flex-1 px-2 py-1.5 transition-colors border-r last:border-r-0 ${
                              op === optVal
                                ? "bg-primary text-primary-foreground"
                                : "hover:bg-muted text-muted-foreground"
                            }`}
                            onClick={() => update(optVal, v1, v2)}
                          >
                            {optVal === "gt" ? "Greater" : optVal === "lt" ? "Less" : "Between"}
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center gap-2">
                        {prefix && <span className="text-sm text-muted-foreground shrink-0">{prefix}</span>}
                        <Input
                          type="number"
                          min={0}
                          placeholder="0"
                          value={v1}
                          onChange={(e) => update(op, e.target.value, v2)}
                          className="bg-background"
                        />
                        {op === "between" && (
                          <>
                            <span className="text-sm text-muted-foreground">–</span>
                            {prefix && <span className="text-sm text-muted-foreground shrink-0">{prefix}</span>}
                            <Input
                              type="number"
                              min={0}
                              placeholder="0"
                              value={v2}
                              onChange={(e) => update(op, v1, e.target.value)}
                              className="bg-background"
                            />
                          </>
                        )}
                        {suffix && <span className="text-sm text-muted-foreground shrink-0">{suffix}</span>}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="flex-shrink-0 px-6 py-4 border-t flex gap-3">
          <Button variant="outline" className="flex-1" onClick={handleCancel}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
